// Console sidebar: the same groups and items as customer-portal's
// config/consoleNav.ts, so every Podstack console has one navigation.
// Inference's own pages are in-app routes; everything else lives in the
// customer portal or the training portal and navigates there in this tab.

import React from 'react';
import {
  HomeIc, GridIc, ChipIc, TerminalIc, DriveIc, LayersIc, TrendIc, ServersIc, BoltIc, ChatIc, KeyIc, DbIc,
  FolderIc, CardIc, SlidersIc, BarsIc, DocsIc,
} from '../components/console/icons';

const trim = (u: string) => u.replace(/\/+$/, '');
const PORTAL_URL = trim(process.env.REACT_APP_CUSTOMER_PORTAL_URL || 'https://cloud.podstack.ai/portal');
const TRAINING_PORTAL_URL = trim(process.env.REACT_APP_TRAINING_PORTAL_URL || 'https://training.podstack.ai/training');

export interface ConsoleNavItem {
  id: string;
  name: string;
  href: string;
  icon: (p: React.SVGProps<SVGSVGElement>) => React.ReactElement;
  /** Another Podstack app: a full-page link instead of an in-app route. */
  external?: boolean;
}

export interface ConsoleNavGroup {
  id: string;
  label?: string;
  hint?: string;
  items: ConsoleNavItem[];
}

// Sandboxes and Managed MLOps are flag-gated in customer-portal; both are on in
// production, so they're listed here unconditionally.
const portal = (id: string, name: string, path: string, icon: ConsoleNavItem['icon']): ConsoleNavItem => ({
  id, name, href: `${PORTAL_URL}${path}`, icon, external: true,
});

export const CONSOLE_NAV: ConsoleNavGroup[] = [
  {
    id: 'start',
    items: [portal('nav-home', 'Home', '/', HomeIc), portal('nav-services', 'All services', '/services', GridIc)],
  },
  {
    id: 'quickpods',
    label: 'QuickPods',
    hint: 'GPU cloud',
    items: [
      portal('nav-pods', 'GPU Pods', '/pods', ChipIc),
      portal('nav-sandboxes', 'Sandboxes', '/sandboxes', TerminalIc),
      portal('nav-volumes', 'NFS volumes', '/volumes', DriveIc),
      portal('nav-managed-mlops', 'Managed MLOps', '/managed-mlops', LayersIc),
    ],
  },
  {
    id: 'trainpods',
    label: 'TrainPods',
    hint: 'Training studio',
    items: [
      { id: 'nav-train-gpus', name: 'GPU catalog', href: `${TRAINING_PORTAL_URL}/console/gpus`, icon: TrendIc, external: true },
      { id: 'nav-train-instances', name: 'Instances', href: `${TRAINING_PORTAL_URL}/console/instances`, icon: ServersIc, external: true },
    ],
  },
  {
    id: 'inference',
    label: 'Inference',
    hint: 'Serverless models',
    items: [
      { id: 'nav-inf-models', name: 'Models', href: '/console/models', icon: BoltIc },
      { id: 'nav-inf-playground', name: 'Playground', href: '/console/playground', icon: ChatIc },
      { id: 'nav-inf-keys', name: 'API keys', href: '/console/keys', icon: KeyIc },
      { id: 'nav-inf-usage', name: 'Usage', href: '/console/usage', icon: BarsIc },
      { id: 'nav-inf-docs', name: 'Docs', href: '/console/docs', icon: DocsIc },
    ],
  },
  {
    id: 'storage',
    label: 'Storage',
    items: [portal('nav-drive', 'Object storage', '/drive', DbIc)],
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      portal('nav-projects', 'Projects', '/projects', FolderIc),
      portal('nav-billing', 'Billing', '/billing', CardIc),
      portal('nav-settings', 'Settings', '/settings', SlidersIc),
    ],
  },
];
